import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Heading, Text, Button, Grid, OverflowMenu } from '../components/atoms';
import { fetchAquariums, deleteAquarium, updateAquarium } from '../services/aquariumService';
import type { Aquarium } from '../services/aquariumService';
import { useTranslation } from '../i18n/useTranslation';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { PageContent } from '../components/templates/PageContent/PageContent';
import { PageHeader } from '../components/templates/PageHeader/PageHeader';
import { AquariumForm } from '../components/AquariumForm';
import type { AquariumFormData } from '../types/aquarium';
import styles from './AquariumSelectionPage.module.css';
import { LoadErrorState } from '../components/feedback/LoadErrorState/LoadErrorState';
import { formatAquariumAge } from '../lib/aquariumAge';

function AquariumPlaceholderIcon() {
  return (
    <svg
      className={styles.cardImagePlaceholder}
      viewBox="0 0 300 180"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      preserveAspectRatio="xMidYMid slice"
    >
      <rect width="300" height="180" fill="var(--color-background-elevated)" />
      <path
        d="M30 130 C70 105 110 145 150 120 C190 95 230 135 270 110"
        stroke="var(--color-primary)"
        strokeWidth="2.5"
        strokeLinecap="round"
        fill="none"
        opacity="0.4"
      />
      <path
        d="M30 150 C70 125 110 160 150 140 C190 118 230 155 270 130"
        stroke="var(--color-primary)"
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
        opacity="0.25"
      />
      <circle cx="90"  cy="90" r="14" fill="var(--color-status-danger)"   opacity="0.35" />
      <circle cx="160" cy="75" r="10" fill="var(--color-status-success)"  opacity="0.35" />
      <circle cx="220" cy="95" r="8"  fill="var(--color-status-info)"     opacity="0.35" />
      <circle cx="130" cy="60" r="6"  fill="var(--color-primary)"          opacity="0.3"  />
    </svg>
  );
}

function SkeletonCard() {
  return (
    <div className={styles.card} aria-hidden="true">
      <div className={styles.skeletonImage} />
      <div className={styles.cardBody}>
        <div className={styles.skeletonName} />
        <div className={styles.cardMeta}>
          <div className={styles.skeletonChip} />
          <div className={styles.skeletonChip} />
        </div>
      </div>
    </div>
  );
}

export function AquariumSelectionPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const { data: aquariums = [], isLoading, isError, refetch } = useQuery({
    queryKey: ['aquariums'],
    queryFn: fetchAquariums,
  });

  const [editingAquarium, setEditingAquarium] = useState<Aquarium | null>(null);
  // `?edit=<id>` opens that aquarium's edit form directly — e.g. from the ZeoVit
  // notice, where the supply system is changed (#1784).
  const [searchParams, setSearchParams] = useSearchParams();
  const editParam = searchParams.get('edit');
  const deepLinkedAquarium = editParam ? (aquariums.find((a) => a.id === editParam) ?? null) : null;
  const formAquarium = editingAquarium ?? deepLinkedAquarium;
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: deleteAquarium,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['aquariums'] });
      setConfirmDeleteId(null);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: AquariumFormData }) =>
      updateAquarium(id, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['aquariums'] });
      closeEditForm();
    },
  });

  function closeEditForm() {
    setEditingAquarium(null);
    if (editParam !== null) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('edit');
          return next;
        },
        { replace: true },
      );
    }
  }

  function handleView(aquarium: Aquarium, e: React.MouseEvent) {
    e.stopPropagation();
    navigate(`/aquariums/${aquarium.id}/timeline`);
  }

  function handleEdit(aquarium: Aquarium, e: React.MouseEvent) {
    e.stopPropagation();
    setEditingAquarium(aquarium);
  }

  function handleDeleteCancel(e: React.MouseEvent) {
    e.stopPropagation();
    setConfirmDeleteId(null);
  }

  function handleDeleteConfirm(id: string, e: React.MouseEvent) {
    e.stopPropagation();
    deleteMutation.mutate(id);
  }

  async function handleEditSubmit(data: AquariumFormData): Promise<void> {
    if (!formAquarium) return;
    await updateMutation.mutateAsync({ id: formAquarium.id, data });
  }

  function handleCreateClick() {
    navigate('/aquariums/new');
  }

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent>
          <PageHeader
            title={t('aquarium.selectionTitle')}
            description={t('aquarium.selectionSubtitle')}
            actions={
              <Button variant="primary" onClick={handleCreateClick}>
                {t('aquarium.addButton')}
              </Button>
            }
          />

          {isLoading ? (
            <Grid minItemWidth="md" gap="6" aria-busy="true">
              {[1, 2, 3].map((n) => <SkeletonCard key={n} />)}
            </Grid>
          ) : isError ? (
            <LoadErrorState title={t('aquarium.loadError')} onRetry={() => void refetch()} />
          ) : aquariums.length === 0 ? (
            <div className={styles.emptyWrapper}>
              <AquariumPlaceholderIcon />
              <Heading level={2}>{t('aquarium.noAquariums')}</Heading>
              <Text>{t('aquarium.emptyDescription')}</Text>
              <Button variant="primary" className={styles.createButton} onClick={handleCreateClick}>
                {t('aquarium.createFirst')}
              </Button>
            </div>
          ) : (
            <Grid minItemWidth="md" gap="6">
              {aquariums.map((aquarium) => {
                // Age of the tank since its setup date (the record's creation date only when none is set).
                const age = formatAquariumAge(aquarium.setupDate ?? aquarium.createdAt, t);
                const isConfirming = confirmDeleteId === aquarium.id;
                const isDeleting = deleteMutation.isPending && confirmDeleteId === aquarium.id;

                return (
                  <div key={aquarium.id} className={styles.card}>
                    {/* Image — pointer shortcut to the same place as the name link
                        (hidden from AT and the tab order to avoid a duplicate stop). */}
                    <Link
                      to={`/aquariums/${aquarium.id}/timeline`}
                      className={styles.cardImageWrap}
                      tabIndex={-1}
                      aria-hidden="true"
                    >
                      {aquarium.imageUrl ? (
                        <img src={aquarium.imageUrl} alt="" className={styles.heroImage} />
                      ) : (
                        <AquariumPlaceholderIcon />
                      )}
                    </Link>

                    <div className={styles.cardBody}>
                      <Heading level={2} className={styles.cardName}>
                        <Link to={`/aquariums/${aquarium.id}/timeline`} className={styles.cardNameLink}>
                          {aquarium.name}
                        </Link>
                      </Heading>
                      <div className={styles.cardMeta}>
                        {aquarium.grossVolume != null && (
                          <span className={styles.metaChip}>
                            {t('aquarium.volume.gross', { value: new Intl.NumberFormat(i18n.language).format(aquarium.grossVolume) })}
                          </span>
                        )}
                        {aquarium.netVolume != null && (
                          <span className={styles.metaChip}>
                            {t('aquarium.volume.net', { value: new Intl.NumberFormat(i18n.language).format(aquarium.netVolume) })}
                          </span>
                        )}
                        {aquarium.tankType && (
                          <span className={styles.metaChip}>{aquarium.tankType}</span>
                        )}
                        {age && (
                          <span className={styles.metaChip}>{t('aquarium.age')}: {age}</span>
                        )}
                      </div>
                    </div>

                    {/* Footer — always visible action row */}
                    {isConfirming ? (
                      <div className={styles.cardConfirmDelete}>
                        <span className={styles.cardConfirmText}>{t('aquarium.confirmDelete')}</span>
                        <Button
                          variant="ghost"
                          className={styles.cardConfirmNo}
                          onClick={handleDeleteCancel}
                          disabled={isDeleting}
                        >
                          {t('common.cancel')}
                        </Button>
                        <Button
                          variant="ghost"
                          className={styles.cardConfirmYes}
                          onClick={(e) => handleDeleteConfirm(aquarium.id, e)}
                          disabled={isDeleting}
                        >
                          {isDeleting ? t('common.processing') : t('common.delete')}
                        </Button>
                      </div>
                    ) : (
                      <div className={styles.cardFooter}>
                        <Button
                          variant="ghost"
                          className={styles.cardActionView}
                          onClick={(e) => handleView(aquarium, e)}
                          aria-label={`${t('common.view')} ${aquarium.name}`}
                        >
                          {t('common.view')} →
                        </Button>
                        <span className={styles.cardActionSpacer} />
                        <Button
                          variant="ghost"
                          className={styles.cardAction}
                          onClick={(e) => handleEdit(aquarium, e)}
                          aria-label={`${t('common.edit')} ${aquarium.name}`}
                        >
                          ✎ {t('common.edit')}
                        </Button>
                        {/* Delete in the "···" menu, then the confirm row (usability audit §5.8). */}
                        <OverflowMenu
                          label={t('common.moreActionsFor', { name: aquarium.name })}
                          items={[
                            { id: 'delete', label: t('common.delete'), onSelect: () => setConfirmDeleteId(aquarium.id), tone: 'danger' },
                          ]}
                        />
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Add new card */}
              <Button
                variant="ghost"
                className={styles.createCard}
                onClick={handleCreateClick}
                aria-label={t('aquarium.addButton')}
              >
                <div className={styles.createCardInner}>
                  <span className={styles.createCardIcon}>+</span>
                  <span>{t('aquarium.addButton')}</span>
                </div>
              </Button>
            </Grid>
          )}

          {/* Edit modal */}
          <AquariumForm
            isOpen={formAquarium !== null}
            onClose={closeEditForm}
            onSubmit={handleEditSubmit}
            aquarium={formAquarium}
            isLoading={updateMutation.isPending}
            error={updateMutation.isError ? updateMutation.error : null}
          />
        </PageContent>
      }
    />
  );
}

export default AquariumSelectionPage;
